import { useEffect, useState, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { fetchAllRows, dashboardMutate } from '@/lib/supabaseHelpers';
import { BookOpen, Trophy, Clock, Pause, Play, Square, ChevronDown, ChevronUp, Sparkles, PenLine, Loader2, Lightbulb, Info, Eye } from 'lucide-react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import ReactMarkdown from 'react-markdown';
import { toast } from 'sonner';
import { sampleFlows } from '@/data/sampleFlows';
import { getScreensFromFlow } from '@/types/flow';
import ScreenRenderer from '@/components/preview/ScreenRenderer';
import adeliraLogo from '@/assets/adelira-logo.png';

// === Statistical significance helpers ===

function normalCDF(z: number): number {
  const a1 = 0.254829592, a2 = -0.284496736, a3 = 1.421413741;
  const a4 = -1.453152027, a5 = 1.061405429, p = 0.3275911;
  const sign = z < 0 ? -1 : 1;
  const x = Math.abs(z) / Math.SQRT2;
  const t = 1.0 / (1.0 + p * x);
  const y = 1.0 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);
  return 0.5 * (1.0 + sign * y);
}

function zTest(n1: number, c1: number, n2: number, c2: number) {
  if (n1 === 0 || n2 === 0) return { z: 0, pValue: 1, significant: false, confidence: 0 };
  const p1 = c1 / n1;
  const p2 = c2 / n2;
  const pPooled = (c1 + c2) / (n1 + n2);
  const se = Math.sqrt(pPooled * (1 - pPooled) * (1 / n1 + 1 / n2));
  if (se === 0) return { z: 0, pValue: 1, significant: false, confidence: 0 };
  const z = (p2 - p1) / se;
  const pValue = 2 * (1 - normalCDF(Math.abs(z)));
  return { z, pValue, significant: pValue <= 0.05, confidence: (1 - pValue) * 100 };
}

interface ABTestEntry {
  id: string;
  name: string;
  description: string | null;
  variant_a_label: string;
  variant_b_label: string;
  started_at: string;
  ended_at: string | null;
  status: string;
  result_snapshot: any;
  winner: string | null;
  notes: string | null;
  ai_learnings: string | null;
  manual_learnings: string | null;
}

interface LiveStats {
  a_pv: number; b_pv: number;
  a_started: number; b_started: number;
  a_completed: number; b_completed: number;
  a_orders: number; b_orders: number;
  a_revenue: number; b_revenue: number;
}

const StatusBadge = ({ status }: { status: string }) => {
  const styles: Record<string, string> = {
    active: 'bg-emerald-100 text-emerald-700',
    completed: 'bg-blue-100 text-blue-700',
    paused: 'bg-amber-100 text-amber-700',
  };
  const icons: Record<string, React.ReactNode> = {
    active: <Play className="w-3 h-3" />,
    completed: <Square className="w-3 h-3" />,
    paused: <Pause className="w-3 h-3" />,
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-600'}`}>
      {icons[status]} {status === 'active' ? 'Actief' : status === 'completed' ? 'Afgerond' : 'Gepauzeerd'}
    </span>
  );
};

const ResultCard = ({ snapshot }: { snapshot: any }) => {
  if (!snapshot) return null;
  return (
    <div className="grid grid-cols-2 gap-2 text-center">
      <div>
        <p className="text-lg font-bold text-gray-700">{snapshot.pageviews}</p>
        <p className="text-[10px] text-gray-400">Pageviews</p>
      </div>
      <div>
        <p className="text-lg font-bold text-blue-600">{snapshot.startRate}%</p>
        <p className="text-[10px] text-gray-400">Start rate</p>
      </div>
      <div>
        <p className="text-lg font-bold text-emerald-600">{snapshot.cvr}%</p>
        <p className="text-[10px] text-gray-400">CVR</p>
      </div>
      <div>
        <p className="text-lg font-bold text-amber-600">€{(snapshot.revenue ?? 0).toFixed(0)}</p>
        <p className="text-[10px] text-gray-400">Omzet ({snapshot.orders ?? 0})</p>
      </div>
    </div>
  );
};

const StatisticalSignificance = ({ aPv, aStarted, aCompleted, bPv, bStarted, bCompleted }: {
  aPv: number; aStarted: number; aCompleted: number; bPv: number; bStarted: number; bCompleted: number;
}) => {
  if (aPv === 0 && bPv === 0) return null;
  const startTest = zTest(aPv, aStarted, bPv, bStarted);
  const cvrTest = zTest(aPv, aCompleted, bPv, bCompleted);

  return (
    <div className="bg-gray-50 rounded-xl p-4 space-y-3">
      <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
        <Info className="w-3.5 h-3.5" /> Statistische significantie
      </h4>
      {[{ label: 'Start rate', test: startTest }, { label: 'Conversie (CVR)', test: cvrTest }].map(({ label, test }) => (
        <div key={label} className="space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">{label}</span>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400">p = {test.pValue.toFixed(4)}</span>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${test.significant ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                {test.significant ? '✓ Significant' : '⏳ Niet significant'}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${test.confidence >= 95 ? 'bg-emerald-500' : test.confidence >= 80 ? 'bg-amber-400' : 'bg-gray-400'}`}
                style={{ width: `${Math.min(test.confidence, 100)}%` }}
              />
            </div>
            <span className={`text-sm font-bold tabular-nums min-w-[3rem] text-right ${test.confidence >= 95 ? 'text-emerald-600' : test.confidence >= 80 ? 'text-amber-600' : 'text-gray-500'}`}>
              {Math.round(test.confidence)}%
            </span>
          </div>
          <p className="text-[10px] text-gray-400">z = {test.z.toFixed(2)} • {test.confidence < 95 ? `Nog ~${Math.max(0, Math.ceil((95 - test.confidence) / 5) * 50)} extra sessies nodig` : 'Voldoende data'}</p>
        </div>
      ))}
    </div>
  );
};

const VisualComparison = () => {
  const flow = sampleFlows.find(f => f.id === '4');
  const rawScreens = flow ? getScreensFromFlow(flow) : [];
  const screen1 = rawScreens[0];
  if (!screen1 || !flow) return null;

  // Variant B: insert social proof between a2 and a3
  const a2Index = screen1.steps.findIndex(s => s.id === 'a2');
  const screenB = a2Index !== -1 ? {
    ...screen1,
    steps: [
      ...screen1.steps.slice(0, a2Index + 1),
      { id: 'a2_social_proof', type: 'text' as const, title: '✅ 5.800+ vrouwen gingen je voor', screenId: screen1.steps[0]?.screenId, settings: { align: 'center' as const }, design: { fontSize: 14, fontSizeMobile: 13, fontWeight: '600', color: '#16a34a', margin: '4px 0 0 0' } },
      ...screen1.steps.slice(a2Index + 1),
    ],
  } : screen1;

  const noop = () => {};

  return (
    <div className="rounded-xl border border-gray-100 overflow-hidden">
      <div className="bg-gray-50 px-4 py-2">
        <p className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
          <Eye className="w-3.5 h-3.5" /> Visuele vergelijking
        </p>
      </div>
      <div className="grid grid-cols-2 gap-0">
        {[{ label: 'A', color: 'blue', screen: screen1, desc: 'Zonder social proof' }, { label: 'B', color: 'purple', screen: screenB, desc: 'Met social proof' }].map(({ label, color, screen, desc }) => (
          <div key={label} className={`border-${color}-200`}>
            <div className={`bg-${color}-50 px-3 py-1.5 flex items-center gap-1.5`}>
              <span className={`inline-flex items-center justify-center w-5 h-5 rounded text-[10px] font-bold bg-${color}-100 text-${color}-700`}>{label}</span>
              <span className={`text-[10px] font-medium text-${color}-800`}>{desc}</span>
            </div>
            <div className="bg-white">
              <div className="bg-[#0F1C2E] h-10 flex items-center justify-center">
                <img src={adeliraLogo} alt="Adelira" className="h-7 object-contain" />
              </div>
              <div className="px-3 py-4 pointer-events-none" style={{ transform: 'scale(0.85)', transformOrigin: 'top center' }}>
                <ScreenRenderer steps={screen.steps} onAdvance={noop} accentColor={flow.accentColor} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ABTestLogbook = () => {
  const [tests, setTests] = useState<ABTestEntry[]>([]);
  const [liveStats, setLiveStats] = useState<LiveStats | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [editingLearnings, setEditingLearnings] = useState<string | null>(null);
  const [manualInput, setManualInput] = useState('');

  const fetchTests = async () => {
    const data = await fetchAllRows<ABTestEntry>('ab_test_log', '*');
    setTests(data.sort((a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime()));
  };

  const fetchLiveStats = async () => {
    // Scope live stats to the active test's start date
    const activeTest = tests.find(t => t.status === 'active');
    const dateFilters = activeTest
      ? [{ column: 'created_at', operator: 'gte', value: activeTest.started_at }]
      : [];

    const [pvData, sessData, ordersData] = await Promise.all([
      fetchAllRows<{ ab_variant: string | null }>('quiz_pageviews', 'ab_variant', [
        { column: 'ab_variant', operator: 'not.is', value: null },
        { column: 'utm_source', operator: 'not.is', value: null },
        ...dateFilters,
      ]),
      fetchAllRows<{ ab_variant: string | null; current_step: number | null; is_completed: boolean | null }>(
        'quiz_sessions', 'ab_variant, current_step, is_completed', [
          { column: 'ab_variant', operator: 'not.is', value: null },
          { column: 'utm_source', operator: 'not.is', value: null },
          ...dateFilters,
        ]
      ),
      fetchAllRows<{ variant: string | null; total_price: number }>('shopify_orders', 'variant, total_price', [
        { column: 'variant', operator: 'not.is', value: null },
        ...(activeTest ? [{ column: 'order_created_at', operator: 'gte', value: activeTest.started_at }] : []),
      ]),
    ]);
    const a_pv = pvData.filter(r => r.ab_variant === 'A').length;
    const b_pv = pvData.filter(r => r.ab_variant === 'B').length;
    const aSess = sessData.filter(r => r.ab_variant === 'A');
    const bSess = sessData.filter(r => r.ab_variant === 'B');

    const sales = { a_orders: 0, b_orders: 0, a_revenue: 0, b_revenue: 0 };
    ordersData.forEach(o => {
      if (o.variant === 'A') { sales.a_orders++; sales.a_revenue += Number(o.total_price); }
      else if (o.variant === 'B') { sales.b_orders++; sales.b_revenue += Number(o.total_price); }
    });

    setLiveStats({
      a_pv, b_pv,
      a_started: aSess.filter(s => (s.current_step ?? 0) >= 2).length,
      b_started: bSess.filter(s => (s.current_step ?? 0) >= 2).length,
      a_completed: aSess.filter(s => s.is_completed).length,
      b_completed: bSess.filter(s => s.is_completed).length,
      ...sales,
    });
  };

  useEffect(() => {
    fetchTests();
  }, []);

  // Re-fetch live stats when tests are loaded (to get active test's started_at)
  useEffect(() => {
    if (tests.length > 0) fetchLiveStats();
  }, [tests]);

  useEffect(() => {
    const channel = supabase
      .channel('logbook-live')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'quiz_sessions' }, () => fetchLiveStats())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'quiz_pageviews' }, () => fetchLiveStats())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'ab_test_log' }, () => fetchTests())
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  const buildSnapshot = () => {
    if (!liveStats) return null;
    return {
      variant_a: {
        pageviews: liveStats.a_pv,
        startRate: liveStats.a_pv > 0 ? Math.round((liveStats.a_started / liveStats.a_pv) * 100) : 0,
        cvr: liveStats.a_pv > 0 ? Math.round((liveStats.a_completed / liveStats.a_pv) * 100) : 0,
        started: liveStats.a_started,
        completed: liveStats.a_completed,
        orders: liveStats.a_orders,
        revenue: liveStats.a_revenue,
      },
      variant_b: {
        pageviews: liveStats.b_pv,
        startRate: liveStats.b_pv > 0 ? Math.round((liveStats.b_started / liveStats.b_pv) * 100) : 0,
        cvr: liveStats.b_pv > 0 ? Math.round((liveStats.b_completed / liveStats.b_pv) * 100) : 0,
        started: liveStats.b_started,
        completed: liveStats.b_completed,
        orders: liveStats.b_orders,
        revenue: liveStats.b_revenue,
      },
    };
  };

  const generateAILearnings = async (test: ABTestEntry, snapshot: any) => {
    setAnalyzing(true);
    try {
      // Gather previous learnings for context
      const completedTests = tests.filter(t => t.id !== test.id && t.status === 'completed');
      const previousLearnings = completedTests
        .map(t => {
          const parts: string[] = [];
          if (t.ai_learnings) parts.push(`AI: ${t.ai_learnings}`);
          if (t.manual_learnings) parts.push(`Handmatig: ${t.manual_learnings}`);
          return parts.length > 0 ? `Test "${t.name}": ${parts.join(' | ')}` : null;
        })
        .filter(Boolean)
        .join('\n');

      const start = new Date(test.started_at);
      const end = test.ended_at ? new Date(test.ended_at) : new Date();
      const durationDays = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

      const { data, error } = await supabase.functions.invoke('analyze-ab-test', {
        body: {
          testName: test.name,
          description: test.description,
          variantALabel: test.variant_a_label,
          variantBLabel: test.variant_b_label,
          resultSnapshot: snapshot,
          winner: snapshot.variant_a.revenue > snapshot.variant_b.revenue ? 'A' :
                  snapshot.variant_b.revenue > snapshot.variant_a.revenue ? 'B' : null,
          durationDays,
          previousLearnings: previousLearnings || null,
        },
      });

      if (error) throw error;
      const aiLearnings = data?.ai_learnings;

      await dashboardMutate('ab_test_log', test.id, { ai_learnings: aiLearnings });
      toast.success('AI-analyse gegenereerd');
      fetchTests();
    } catch (e) {
      console.error(e);
      toast.error('AI-analyse mislukt');
    } finally {
      setAnalyzing(false);
    }
  };

  const stopTest = async (test: ABTestEntry) => {
    const snapshot = buildSnapshot();
    if (!snapshot) return;

    const revA = snapshot.variant_a.revenue;
    const revB = snapshot.variant_b.revenue;
    const winner = revA > revB ? 'A' : revB > revA ? 'B' : null;

    await dashboardMutate('ab_test_log', test.id, {
      status: 'completed',
      ended_at: new Date().toISOString(),
      result_snapshot: snapshot,
      winner,
    });

    toast.success('Test gestopt, resultaten opgeslagen');

    // Generate AI learnings automatically
    await generateAILearnings({ ...test, status: 'completed' }, snapshot);
    fetchTests();
  };

  const saveManualLearnings = async (testId: string) => {
    await dashboardMutate('ab_test_log', testId, { manual_learnings: manualInput });
    setEditingLearnings(null);
    toast.success('Learnings opgeslagen');
    fetchTests();
  };

  const getDuration = (test: ABTestEntry) => {
    const start = new Date(test.started_at);
    const end = test.ended_at ? new Date(test.ended_at) : new Date();
    const days = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    return `${days} dag${days !== 1 ? 'en' : ''}`;
  };

  // Collect all learnings from completed tests for the "knowledge base" section
  const completedWithLearnings = tests.filter(t => t.status === 'completed' && (t.ai_learnings || t.manual_learnings));

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
      <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
        <BookOpen className="w-4 h-4" />
        A/B Test Logboek
      </h3>

      {/* Accumulated Learnings Summary */}
      {completedWithLearnings.length > 0 && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-4">
          <h4 className="text-sm font-semibold text-amber-800 flex items-center gap-1.5 mb-2">
            <Lightbulb className="w-4 h-4" />
            Opgebouwde kennis ({completedWithLearnings.length} test{completedWithLearnings.length !== 1 ? 's' : ''})
          </h4>
          <div className="space-y-2">
            {completedWithLearnings.map(t => (
              <div key={t.id} className="text-xs text-amber-700">
                <span className="font-medium">"{t.name}"</span>
                {t.winner && <span className="text-amber-600"> — Winnaar: Variant {t.winner}</span>}
                {t.manual_learnings && (
                  <p className="text-amber-600 mt-0.5 ml-2">📝 {t.manual_learnings}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-3">
        {tests.map(test => {
          const isExpanded = expandedId === test.id;
          const snap = test.result_snapshot as any;

          return (
            <div key={test.id} className="border border-gray-100 rounded-xl overflow-hidden">
              <button
                onClick={() => setExpandedId(isExpanded ? null : test.id)}
                className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50 transition"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <StatusBadge status={test.status} />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{test.name}</p>
                    <p className="text-xs text-gray-400 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {format(new Date(test.started_at), 'd MMM yyyy', { locale: nl })}
                      {test.ended_at && <> → {format(new Date(test.ended_at), 'd MMM yyyy', { locale: nl })}</>}
                      {' · '}{getDuration(test)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {test.winner && (
                    <span className="flex items-center gap-1 text-xs font-medium text-emerald-600">
                      <Trophy className="w-3 h-3" /> Variant {test.winner}
                    </span>
                  )}
                  {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                </div>
              </button>

              {isExpanded && (
                <div className="px-4 pb-4 space-y-3 border-t border-gray-50">
                  {test.description && (
                    <p className="text-xs text-gray-500 pt-3">{test.description}</p>
                  )}

                  {/* Variant Results */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-lg bg-blue-50/50 p-3">
                      <p className="text-xs font-medium text-blue-700 mb-1">A: {test.variant_a_label}</p>
                      {snap?.variant_a ? (
                        <ResultCard snapshot={snap.variant_a} />
                      ) : test.status === 'active' && liveStats ? (
                        <ResultCard snapshot={{
                          pageviews: liveStats.a_pv,
                          startRate: liveStats.a_pv > 0 ? Math.round((liveStats.a_started / liveStats.a_pv) * 100) : 0,
                          cvr: liveStats.a_pv > 0 ? Math.round((liveStats.a_completed / liveStats.a_pv) * 100) : 0,
                          orders: liveStats.a_orders,
                          revenue: liveStats.a_revenue,
                        }} />
                      ) : <p className="text-xs text-gray-400">Geen data</p>}
                    </div>
                    <div className="rounded-lg bg-purple-50/50 p-3">
                      <p className="text-xs font-medium text-purple-700 mb-1">B: {test.variant_b_label}</p>
                      {snap?.variant_b ? (
                        <ResultCard snapshot={snap.variant_b} />
                      ) : test.status === 'active' && liveStats ? (
                        <ResultCard snapshot={{
                          pageviews: liveStats.b_pv,
                          startRate: liveStats.b_pv > 0 ? Math.round((liveStats.b_started / liveStats.b_pv) * 100) : 0,
                          cvr: liveStats.b_pv > 0 ? Math.round((liveStats.b_completed / liveStats.b_pv) * 100) : 0,
                          orders: liveStats.b_orders,
                          revenue: liveStats.b_revenue,
                        }} />
                      ) : <p className="text-xs text-gray-400">Geen data</p>}
                    </div>
                  </div>

                  {/* Winner */}
                  {test.winner && snap && (
                    <div className="bg-emerald-50 rounded-lg p-3 text-center">
                      <p className="text-sm font-medium text-emerald-700">
                        🏆 Variant {test.winner} won met €{(test.winner === 'A' ? snap.variant_a?.revenue : snap.variant_b?.revenue)?.toFixed(0) ?? '0'} omzet
                        vs €{(test.winner === 'A' ? snap.variant_b?.revenue : snap.variant_a?.revenue)?.toFixed(0) ?? '0'}
                      </p>
                    </div>
                  )}

                  {/* Statistical Significance */}
                  <StatisticalSignificance
                    aPv={snap?.variant_a?.pageviews ?? liveStats?.a_pv ?? 0}
                    aStarted={snap?.variant_a?.started ?? liveStats?.a_started ?? 0}
                    aCompleted={snap?.variant_a?.completed ?? liveStats?.a_completed ?? 0}
                    bPv={snap?.variant_b?.pageviews ?? liveStats?.b_pv ?? 0}
                    bStarted={snap?.variant_b?.started ?? liveStats?.b_started ?? 0}
                    bCompleted={snap?.variant_b?.completed ?? liveStats?.b_completed ?? 0}
                  />

                  {/* Visual Comparison */}
                  <VisualComparison />

                  {/* AI Learnings */}
                  {test.ai_learnings && (
                    <div className="bg-indigo-50 rounded-lg p-3">
                      <p className="text-xs font-semibold text-indigo-700 flex items-center gap-1 mb-1.5">
                        <Sparkles className="w-3 h-3" /> AI Analyse
                      </p>
                      <div className="text-xs text-indigo-800 prose prose-xs max-w-none">
                        <ReactMarkdown>{test.ai_learnings}</ReactMarkdown>
                      </div>
                    </div>
                  )}

                  {/* Manual Learnings */}
                  {test.manual_learnings && editingLearnings !== test.id && (
                    <div className="bg-amber-50 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-1.5">
                        <p className="text-xs font-semibold text-amber-700 flex items-center gap-1">
                          <PenLine className="w-3 h-3" /> Jouw notities
                        </p>
                        <button
                          onClick={() => { setEditingLearnings(test.id); setManualInput(test.manual_learnings || ''); }}
                          className="text-[10px] text-amber-600 hover:text-amber-800"
                        >
                          Bewerken
                        </button>
                      </div>
                      <p className="text-xs text-amber-800">{test.manual_learnings}</p>
                    </div>
                  )}

                  {/* Manual Learnings Input */}
                  {(editingLearnings === test.id || (test.status === 'completed' && !test.manual_learnings)) && (
                    <div className="space-y-2">
                      <textarea
                        value={editingLearnings === test.id ? manualInput : ''}
                        onChange={(e) => { setManualInput(e.target.value); if (editingLearnings !== test.id) setEditingLearnings(test.id); }}
                        placeholder="Voeg je eigen learnings toe... bijv. 'Kwalificatie-framing werkt beter bij koude traffic'"
                        className="w-full text-xs p-3 rounded-lg border border-gray-200 resize-none h-20 focus:outline-none focus:ring-2 focus:ring-amber-300"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => saveManualLearnings(test.id)}
                          disabled={!manualInput.trim()}
                          className="px-3 py-1.5 rounded-lg bg-amber-100 text-amber-700 text-xs font-medium hover:bg-amber-200 transition disabled:opacity-50"
                        >
                          <PenLine className="w-3 h-3 inline mr-1" /> Opslaan
                        </button>
                        {editingLearnings === test.id && (
                          <button
                            onClick={() => setEditingLearnings(null)}
                            className="px-3 py-1.5 rounded-lg text-gray-500 text-xs hover:bg-gray-100 transition"
                          >
                            Annuleren
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Generate AI Analysis button for completed tests without AI learnings */}
                  {test.status === 'completed' && !test.ai_learnings && snap && (
                    <button
                      onClick={() => generateAILearnings(test, snap)}
                      disabled={analyzing}
                      className="w-full py-2 px-4 rounded-lg bg-indigo-50 text-indigo-600 text-xs font-medium hover:bg-indigo-100 transition flex items-center justify-center gap-1.5 disabled:opacity-50"
                    >
                      {analyzing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                      {analyzing ? 'Analyseren...' : 'AI Analyse genereren'}
                    </button>
                  )}

                  {/* Stop test button */}
                  {test.status === 'active' && (
                    <button
                      onClick={() => stopTest(test)}
                      disabled={analyzing}
                      className="w-full mt-2 py-2 px-4 rounded-lg bg-red-50 text-red-600 text-xs font-medium hover:bg-red-100 transition flex items-center justify-center gap-1.5 disabled:opacity-50"
                    >
                      {analyzing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Square className="w-3 h-3" />}
                      {analyzing ? 'Test stoppen & analyseren...' : 'Test stoppen & resultaten opslaan'}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {tests.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-4">Nog geen tests gelogd</p>
        )}
      </div>
    </div>
  );
};

export default ABTestLogbook;
